import { Capacitor } from '@capacitor/core'
import {
  AppBar,
  Box,
  Button,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import { IconButton, Icons, SkeletonText } from 'utoniq-ui'
import { InformationModal } from 'utoniq-ui'

import {
  ArtistMembershipQuery,
  ArtistWithSubscriptionFragment,
  Maybe,
  MembershipSubscriptionPlanDetailFragment,
  MembershipSubscriptionProductDetailFragment,
  PatronType,
  SubscriptionDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, HeaderImageArea } from '@/components/layouts'
import { ProductBox, RequiredMembershipInfoBox } from '@/components/organisms'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import {
  ArtistThemeProvider,
  styles,
  useArtistThemeContext,
} from '@/components/styles'
import { config, constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { captureError } from '@/core/errors'
import { MembershipSubscriptionProduct } from '@/core/membership'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  subscribing: boolean
  isLoggedIn: boolean
  isMember: boolean
  fetchingDiffAmountSubscription: boolean
  appName?: string
  stopSubscription: boolean
  isPersonalInformationRequired: boolean
  isMembershipPersonalInformationRequired: boolean
  requireRegisteredCredit: boolean
  artist: Maybe<ArtistMembershipQuery['artist']>
  products: Maybe<MembershipSubscriptionProductDetailFragment[]>
  pendingPurchasePatron: Maybe<
    ArtistWithSubscriptionFragment['pendingPurchasePatron']
  >
  activeProduct?: MembershipSubscriptionProduct
  downgradeProduct?: MembershipSubscriptionProduct
  subscription: Maybe<SubscriptionDetailFragment>
  expiredAtSubscription: Maybe<string>
  diffAmountSubscription: number
  downgradeAtNextPeriod: boolean
  patronType: Maybe<PatronType>
  store: Maybe<string>
}

export type Action = {
  onSubscribePlan: (planId: string) => Promise<void>
  onDowngradePlan: (planId: string) => Promise<void>
  onUpgradePlan: (planId: string) => Promise<void>
  onPreviewSubscriptionUpdate: (planId: string) => Promise<void>
  onStopSubscription: () => Promise<void>
  onCancelDowngradePlan: () => Promise<void>
}

export const ArtistMembershipTemplate: Template<State, Action> = ({
  state,
  onSubscribePlan,
  onDowngradePlan,
  onUpgradePlan,
  onPreviewSubscriptionUpdate,
  onStopSubscription,
  onCancelDowngradePlan,
}) => {
  const { t } = useTranslation('artistMembership')
  const { router, routerNames, generatePath, routerPages } = useRouter()
  const defaultBackPage = generatePath(routerPages.ArtistTop, {
    artistId: state.artist?.id ?? '#',
  })
  const { handleLoginModal } = usePrivateAction()
  const { showMessage, showError } = useMessage()
  const [currentSelectPlan, setCurrentSelectPlan] =
    useState<MembershipSubscriptionPlanDetailFragment>()
  const [openConfirm, setOpenConfirm] = useState(false)
  const [openDowngradeConfirm, setOpenDowngradeConfirm] = useState(false)
  const [openUpgradeConfirm, setOpenUpgradeConfirm] = useState(false)
  const [openStopConfirm, setOpenStopConfirm] = useState(false)
  const [openCancelDowngradeConfirm, setOpenCancelDowngradeConfirm] =
    useState(false)
  const [openKonbiniUpgradeConfirm, setOpenKonbiniUpgradeConfirm] =
    useState(false)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  const handleSubscribePlan = useCallback(async () => {
    if (!currentSelectPlan?.planId) return
    await onSubscribePlan(currentSelectPlan.planId)
      .then(() => showMessage(t('completeSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(e)
      })
    setOpenConfirm(false)
  }, [currentSelectPlan?.planId, onSubscribePlan, showError, showMessage, t])

  const handleDowngradePlan = useCallback(async () => {
    if (!currentSelectPlan?.planId) return
    await onDowngradePlan(currentSelectPlan.planId)
      .then(() => showMessage(t('completeDowngradeSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(e)
      })
    setOpenDowngradeConfirm(false)
  }, [currentSelectPlan?.planId, onDowngradePlan, showError, showMessage, t])

  const handleUpgradePlan = useCallback(async () => {
    if (!currentSelectPlan?.planId) return
    await onUpgradePlan(currentSelectPlan.planId)
      .then(() => showMessage(t('completeUpgradeSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
          /**
           * @description
           * 3Dセキュア認証が必要な場合
           */
          case constants.errorCodes.REQUIRE_3D_SECURE:
            setOpenRequire3DSecureModal(true)
            break
          /**
           * @description
           * カードが有効期限切れの場合
           */
          case constants.errorCodes.EXPIRED_CARD:
            setOpenExpiredCardModal(true)
            break
          /**
           * @description
           * 何かしらの決済エラーの場合
           */
          case constants.errorCodes.PAYMENT_ERROR:
            setOpenPaymentErrorModal(true)
            break
          default:
            showError(e)
            break
        }
      })
    setOpenUpgradeConfirm(false)
  }, [currentSelectPlan?.planId, onUpgradePlan, showError, showMessage, t])

  const handleStopSubscription = useCallback(async () => {
    await onStopSubscription()
      .then(() => showMessage(t('completeStopSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(new Error(t('error')))
      })
    setOpenStopConfirm(false)
  }, [onStopSubscription, showError, showMessage, t])

  const handleCancelDowngradePlan = useCallback(async () => {
    await onCancelDowngradePlan()
      .then(() => showMessage(t('completeCancelDowngradeSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(new Error(t('error')))
      })
    setOpenCancelDowngradeConfirm(false)
  }, [onCancelDowngradePlan, showError, showMessage, t])

  const isNative = Capacitor.isNativePlatform()

  return (
    <ArtistThemeProvider>
      <Head title={[routerNames.ArtistMembership, state.artist?.displayName]} />

      <BaseLayout safeArea={{ pt: 0 }}>
        {state.loading ? (
          <ArtistMemberLoadingCell
            onClose={() => router.goBack(defaultBackPage)}
          />
        ) : (
          <>
            <BannerCell
              artist={state.artist}
              onClose={() => router.goBack(defaultBackPage)}
            />
            <Box
              sx={{
                mt: state.appName ? '-48px' : '-60px',
                px: 5,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Typography variant="h2" sx={{ py: 3 }} align="center">
                {/* ろこまこあこアプリの場合、表示名をアプリ名に */}
                {config.applicationKey === 'rocomacoaco'
                  ? config.appName
                  : state.artist?.displayName}
              </Typography>
              <Typography variant="body1">
                {t('memberDesc', {
                  displayName: state.artist?.displayName ?? '',
                })}
              </Typography>
            </Box>
            {state.isPersonalInformationRequired &&
              state.isMembershipPersonalInformationRequired && (
                <Box px={5} pt={5} pb={1}>
                  <RequiredMembershipInfoBox visibleLink />
                </Box>
              )}
            {!state.appName && (
              <Stack sx={{ px: 5 }}>
                <Stack spacing={5} sx={{ pt: 4, pb: 6 }}>
                  {state.products?.map(product => (
                    <ProductBox
                      key={product.productId}
                      product={product}
                      expiredAt={state.expiredAtSubscription}
                      activeProduct={state.activeProduct}
                      downgradeProduct={state.downgradeProduct}
                      loading={
                        product.plans.some(
                          plan => currentSelectPlan?.planId === plan.planId
                        ) && state.fetchingDiffAmountSubscription
                      }
                      pendingPurchasePatron={state.pendingPurchasePatron}
                      // プランごとに表示されるボタンを押したときの挙動
                      onClickButton={(event, plan) => {
                        handleLoginModal(async () => {
                          // テンプレートの状態に「現在選択しているプラン」をセット
                          setCurrentSelectPlan(plan)

                          /**
                           * ネイティブの場合は複数プランが作れないので、サブスク購入モーダルを開いてreturn
                           */
                          if (isNative) {
                            setOpenConfirm(true)
                            return
                          }

                          // プランのステータス毎のモーダルの表示
                          switch (plan?.planRelationship) {
                            // ダウングレードのキャンセル
                            case 'forLeave':
                            case 'forJoinedUpper':
                            case 'forJoinedLower':
                              return setOpenCancelDowngradeConfirm(true)
                            // ダウングレード
                            case 'lower':
                              return setOpenDowngradeConfirm(true)
                            // アップグレード
                            case 'upper':
                              await onPreviewSubscriptionUpdate(plan.planId)
                              return setOpenUpgradeConfirm(true)
                            // アップグレード
                            case 'konbini_upper':
                              return setOpenKonbiniUpgradeConfirm(true)
                            // ダウングレード
                            case 'konbini_lower':
                              return router.push(
                                generatePath(
                                  routerPages.ArtistMembershipPaymentEdit,
                                  {
                                    artistId: state.artist?.id ?? '#',
                                    productId: product.productId ?? '#',
                                  }
                                )
                              )
                            // サブスクの停止
                            case 'joined':
                              return setOpenStopConfirm(true)
                            // サブスクを開始
                            case 'unjoined':
                            default:
                              return router.push(
                                generatePath(
                                  routerPages.ArtistMembershipPurchaseProceed,
                                  {
                                    artistId: state.artist?.id,
                                    productId: product.productId?.toString(),
                                  }
                                )
                              )
                          }
                        })(event as never)
                      }}
                      patronType={state.patronType}
                      store={state.store}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
            {!!state.appName && (
              <>
                <WebInductionCell appName={state.appName} />
              </>
            )}
          </>
        )}
        <InformationModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          title={t('confirm')}
          content={t('confirmContent', {
            price: currentSelectPlan?.amount ?? 0,
          })}
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleSubscribePlan}
              loading={state.subscribing}
            >
              {t('becomeMember')}
            </Button>
          }
        />
        <InformationModal
          open={openDowngradeConfirm}
          onClose={() => setOpenDowngradeConfirm(false)}
          title={t('downgradeConfirm')}
          content={
            <Stack spacing={3} sx={{ pb: 7 }}>
              <Typography variant="body2">
                {t('downgradeConfirmContent')}
              </Typography>
              <Typography variant="body2">
                {t('downgradeConfirmExpired', {
                  expiredAt: DateTime.formatDate(state.expiredAtSubscription),
                })}
              </Typography>
            </Stack>
          }
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleDowngradePlan}
              loading={state.subscribing}
            >
              {t('downgrade')}
            </Button>
          }
        />
        <InformationModal
          open={openUpgradeConfirm}
          onClose={() => setOpenUpgradeConfirm(false)}
          title={t('upgradeConfirm')}
          content={t('upgradeConfirmContent', {
            diff: state.diffAmountSubscription,
          })}
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleUpgradePlan}
              loading={state.subscribing}
            >
              {t('upgrade')}
            </Button>
          }
        />
        <InformationModal
          open={openStopConfirm}
          onClose={() => setOpenStopConfirm(false)}
          title={t('stopConfirm')}
          content={t('stopConfirmContent')}
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleStopSubscription}
              loading={state.subscribing}
            >
              {t('stop')}
            </Button>
          }
        />
        <InformationModal
          open={openCancelDowngradeConfirm}
          onClose={() => setOpenCancelDowngradeConfirm(false)}
          title={t('cancelDowngradeConfirm')}
          content={t('cancelDowngradeConfirmContent')}
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleCancelDowngradePlan}
              loading={state.subscribing}
            >
              {t('cancelDowngrade')}
            </Button>
          }
        />
        <InformationModal
          open={openKonbiniUpgradeConfirm}
          onClose={() => setOpenKonbiniUpgradeConfirm(false)}
          title={t('konbiniUpgradeConfirm')}
          content={
            <Stack spacing={3} pt={3}>
              <Typography variant="body2" align="left">
                {t('konbiniUpgradeConfirmContent')}
              </Typography>
              <Typography variant="body2" align="center">
                {t('nextUpdatedAt', { date: state.expiredAtSubscription })}
              </Typography>
            </Stack>
          }
          buttons={
            <Stack spacing={4} pt={7}>
              <Button
                size="large"
                sx={{ minWidth: 212 }}
                href={generatePath(routerPages.ArtistMembershipPaymentEdit, {
                  artistId: state.artist?.id ?? '#',
                  productId:
                    state.products?.find(product =>
                      product.plans.some(
                        plan => plan.planId === currentSelectPlan?.planId
                      )
                    )?.productId ?? '#',
                })}
              >
                {t('changePaymentMethod')}
              </Button>
              <Button
                size="large"
                variant="outlined"
                sx={{ minWidth: 212 }}
                onClick={() => setOpenKonbiniUpgradeConfirm(false)}
              >
                閉じる
                {/* NOTE: 更新時アップグレードのAPIがないので、現在は閉じるだけ */}
                {/* {t('upgradeOnRenewal')} */}
              </Button>
            </Stack>
          }
        />
        <ExpiredCardModal
          open={openExpiredCardModal}
          onClose={() => setOpenExpiredCardModal(false)}
        />

        <PaymentErrorModal
          open={openPaymentErrorModal}
          onClose={() => setOpenPaymentErrorModal(false)}
        />

        <Require3DSecureModal
          open={openRequire3DSecureModal}
          onClose={() => setOpenRequire3DSecureModal(false)}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type BannerCellProps = {
  artist: State['artist']
  onClose: () => void
}

const BannerCell: Cell<BannerCellProps> = ({ artist, onClose, children }) => {
  const [{ bgImage }] = useArtistThemeContext()

  return (
    <HeaderImageArea
      hasSafeArea
      gradationMask
      bgImage={bgImage}
      imageUri={artist?.avatar?.mediumUri}
    >
      <CellFrame
        sx={{
          width: '100%',
          height: { xs: 280, md: 584 },
        }}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <AppBar
          position="static"
          sx={{
            bgcolor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            color: 'text.primary',
          }}
        >
          <Toolbar>
            <IconButton.Close onClick={onClose} bgcolor="grey.50" />
          </Toolbar>
        </AppBar>
      </CellFrame>
    </HeaderImageArea>
  )
}

type ArtistMemberLoadingCellProps = {
  onClose: () => void
}
const ArtistMemberLoadingCell: Cell<ArtistMemberLoadingCellProps> = ({
  onClose,
}) => (
  <CellFrame>
    <Skeleton
      variant="rectangular"
      width="100%"
      sx={{
        height: { xs: 280, md: 584 },
        '& header': { visibility: 'visible' },
      }}
    >
      <AppBar
        position="static"
        sx={{
          pt: styles.safeAreaTop,
          bgcolor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <IconButton.Close onClick={onClose} bgcolor="grey.50" />
        </Toolbar>
      </AppBar>
    </Skeleton>
    <Stack sx={{ px: 5, mt: '60px', pt: 5 }} spacing={5}>
      <Skeleton
        variant="rectangular"
        height={348}
        width="100%"
        sx={{ borderRadius: 2 }}
      />
      <Skeleton
        variant="rectangular"
        height={348}
        width="100%"
        sx={{ borderRadius: 2 }}
      />
      <Skeleton
        variant="rectangular"
        height={348}
        width="100%"
        sx={{ borderRadius: 2 }}
      />
    </Stack>
    <Stack sx={{ px: 5, py: 7 }}>
      <SkeletonText
        sx={{ py: 1 }}
        variant="small"
        numberOfLine={5}
        lastWidth="75%"
      />
    </Stack>
  </CellFrame>
)

type WebInductionCellProps = {
  appName: string
}
export const WebInductionCell: Cell<WebInductionCellProps> = ({ appName }) => {
  const { t } = useTranslation('artistMembership')
  return (
    <CellFrame sx={{ mt: 4, mx: 5, mb: -2 }}>
      <Stack
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: 'grey.100',
          pt: 5,
          px: 7,
          pb: 6,
        }}
        alignItems="center"
      >
        <Typography variant="body2">{t('cannotOpen')}</Typography>
        <Typography variant="subtitle1" color="text.hint" sx={{ mt: 4 }}>
          {t('searchBrowser')}
        </Typography>
        <Button
          endIcon={
            <Box>
              <Icons.Search fontSize="medium" />
            </Box>
          }
          sx={{ width: 275, mt: 2 }}
          disabled
        >
          <Typography variant="subtitle1" width="100%" sx={{ ml: 7 }}>
            {appName}
          </Typography>
        </Button>
      </Stack>
    </CellFrame>
  )
}
